"use client"

import { useState } from "react"
import StatCell from "../generic/StatCell"
import { QSTATUS } from "../../../../../apps/frontend/src/utils/strings"
import TableLoader from "../generic/TableLoader"
import { Switch } from "../../components/ui/switch"
import TableHeader from "../../components/generic/TableHeader"
import NoData from "../generic/NoData"
import React from "react"

const DifficultySummary = ({ data, loading, error, onResetFilters }) => {
  const [showPercentage, setShowPercentage] = useState(true)
  const [selectedSubject, setSelectedSubject] = useState("overall")

  // Filter data based on selected subject
  const filteredData = selectedSubject === "overall" 
    ? data 
    : data.filter(subject => subject.subjectMeta._id === selectedSubject)

  // Calculate totals for each difficulty level
const calculateTotals = (difficulty) => {
  let correct = 0
  let incorrect = 0
  let unanswered = 0
  let total = 0

  if (!Array.isArray(data)) return { correct, incorrect, unanswered, total }

  data.forEach(subject => {
    correct += subject[difficulty]?.correct || 0
    incorrect += subject[difficulty]?.incorrect || 0
    unanswered += subject[difficulty]?.unanswered || 0
    total += (subject[difficulty]?.correct || 0) + 
             (subject[difficulty]?.incorrect || 0) + 
             (subject[difficulty]?.unanswered || 0)
  })

  return { correct, incorrect, unanswered, total }
}


  const easyTotals = calculateTotals("easy")
  const moderateTotals = calculateTotals("moderate")
  const toughTotals = calculateTotals("tough")

 return (
  <div className="border border-[var(--q3-stroke-light)] rounded-lg">
    <TableHeader
      headerTitle="Summary"
      showBottomBorder={false}
    >
      {/* Horizontal Scroll Wrapper */}
      <div className="overflow-x-auto">
        <div className="min-w-[600px] border border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">

          {/* Header */}
          <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
            <div className="grid grid-cols-4 divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]">
              <div className="p-4 text-[var(--q3-neutral-default)]">Difficulty</div>
              <div className="p-4 text-center text-[var(--q3-green-normal)]">Attempted Correct</div>
              <div className="p-4 text-center text-[var(--q3-danger-normal)]">Attempted Wrong</div>
              <div className="p-4 text-center text-[var(--q3-neutral-light)]">Not Attempted</div>
            </div>
          </div>

          {/* Data Rows */}
          <TableLoader loading={loading}>
            {error ? (
              <div className="text-red-500 p-4">Error loading data: {error.message}</div>
            ) : !data || data.length === 0 ? (
              <NoData 
                onResetAll={onResetFilters}
              />
            ) : (
              <>
                {/* EASY Row */}
                <div className="border-b border-[var(--q3-stroke-light)]">
                  <div className="grid grid-cols-4 divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]">
                    <div className="p-4 font-medium">Easy</div>
                    <div className="text-center text-green-600">
                      <StatCell
                        {...QSTATUS.CORRECT}
                        top={selectedSubject === "overall" ? easyTotals.correct : filteredData[0]?.easy?.correct}
                        bottom={selectedSubject === "overall" ? easyTotals.total : (filteredData[0]?.easy?.correct + filteredData[0]?.easy?.incorrect + filteredData[0]?.easy?.unanswered)}
                        showPercentage={showPercentage}
                      />
                    </div>
                    <div className="text-center text-red-600">
                      <StatCell
                        {...QSTATUS.INCORRECT}
                        top={selectedSubject === "overall" ? easyTotals.incorrect : filteredData[0]?.easy?.incorrect}
                        bottom={selectedSubject === "overall" ? easyTotals.total : (filteredData[0]?.easy?.correct + filteredData[0]?.easy?.incorrect + filteredData[0]?.easy?.unanswered)}
                        showPercentage={showPercentage}
                      />
                    </div>
                    <div className="text-center text-gray-600">
                      <StatCell
                        {...QSTATUS.NOT_VISITED}
                        top={selectedSubject === "overall" ? easyTotals.unanswered : filteredData[0]?.easy?.unanswered}
                        bottom={selectedSubject === "overall" ? easyTotals.total : (filteredData[0]?.easy?.correct + filteredData[0]?.easy?.incorrect + filteredData[0]?.easy?.unanswered)}
                        showPercentage={showPercentage}
                        themedText={true}
                      />
                    </div>
                  </div>
                </div>

                {/* MODERATE Row */}
                <div className="border-b border-[var(--q3-stroke-light)]">
                  <div className="grid grid-cols-4 divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]">
                    <div className="p-4 font-medium">Moderate</div>
                    <div className="text-center text-green-600">
                      <StatCell
                        {...QSTATUS.CORRECT}
                        top={selectedSubject === "overall" ? moderateTotals.correct : filteredData[0]?.moderate?.correct}
                        bottom={selectedSubject === "overall" ? moderateTotals.total : (filteredData[0]?.moderate?.correct + filteredData[0]?.moderate?.incorrect + filteredData[0]?.moderate?.unanswered)}
                        showPercentage={showPercentage}
                      />
                    </div>
                    <div className="text-center text-red-600">
                      <StatCell
                        {...QSTATUS.INCORRECT}
                        top={selectedSubject === "overall" ? moderateTotals.incorrect : filteredData[0]?.moderate?.incorrect}
                        bottom={selectedSubject === "overall" ? moderateTotals.total : (filteredData[0]?.moderate?.correct + filteredData[0]?.moderate?.incorrect + filteredData[0]?.moderate?.unanswered)}
                        showPercentage={showPercentage}
                      />
                    </div>
                    <div className="text-center text-gray-600">
                      <StatCell
                        {...QSTATUS.NOT_VISITED}
                        top={selectedSubject === "overall" ? moderateTotals.unanswered : filteredData[0]?.moderate?.unanswered}
                        bottom={selectedSubject === "overall" ? moderateTotals.total : (filteredData[0]?.moderate?.correct + filteredData[0]?.moderate?.incorrect + filteredData[0]?.moderate?.unanswered)}
                        showPercentage={showPercentage}
                        themedText={true}
                      />
                    </div>
                  </div>
                </div>

                {/* TOUGH Row */}
                <div className="border-b border-[var(--q3-stroke-light)] last:border-b-0">
                  <div className="grid grid-cols-4 divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]">
                    <div className="p-4 font-medium">Tough</div>
                    <div className="text-center text-green-600">
                      <StatCell
                        {...QSTATUS.CORRECT}
                        top={selectedSubject === "overall" ? toughTotals.correct : filteredData[0]?.tough?.correct}
                        bottom={selectedSubject === "overall" ? toughTotals.total : (filteredData[0]?.tough?.correct + filteredData[0]?.tough?.incorrect + filteredData[0]?.tough?.unanswered)}
                        showPercentage={showPercentage}
                      />
                    </div>
                    <div className="text-center text-red-600">
                      <StatCell
                        {...QSTATUS.INCORRECT}
                        top={selectedSubject === "overall" ? toughTotals.incorrect : filteredData[0]?.tough?.incorrect}
                        bottom={selectedSubject === "overall" ? toughTotals.total : (filteredData[0]?.tough?.correct + filteredData[0]?.tough?.incorrect + filteredData[0]?.tough?.unanswered)}
                        showPercentage={showPercentage}
                      />
                    </div>
                    <div className="text-center text-gray-600">
                      <StatCell
                        {...QSTATUS.NOT_VISITED}
                        top={selectedSubject === "overall" ? toughTotals.unanswered : filteredData[0]?.tough?.unanswered}
                        bottom={selectedSubject === "overall" ? toughTotals.total : (filteredData[0]?.tough?.correct + filteredData[0]?.tough?.incorrect + filteredData[0]?.tough?.unanswered)}
                        showPercentage={showPercentage}
                        themedText={true}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </TableLoader>
        </div>
      </div>
    </TableHeader>
  </div>
);

}

export default DifficultySummary